// src/components/tutorial/InteractiveWalkthrough.tsx
/**
 * Interactive Walkthrough Component
 *
 * Step-by-step guide for the first combination
 */

import React, { useState, useEffect } from 'react';
import { Hand, Sparkles, ArrowRight, Target, X } from 'lucide-react';
import { colors } from '../../theme/designTokens';
import { triggerHaptic } from '../../services/capacitorService';
import {
  markFirstCombinationComplete,
  updateTutorialStep,
  loadTutorialState,
} from '../../services/tutorialService';

interface InteractiveWalkthroughProps {
  onComplete: () => void;
  onSkip: () => void;
  currentStep?: number; // Current game state step (optional for auto-progression)
  onStepChange?: (step: number) => void; // Callback when step changes
}

interface WalkthroughStep {
  title: string;
  message: string;
  highlight?: string; // CSS selector or description of what to highlight
  action: string; // What the user should do
  icon: React.ReactNode;
  position: 'top' | 'bottom' | 'center';
}

const InteractiveWalkthrough: React.FC<InteractiveWalkthroughProps> = ({
  onComplete,
  onSkip,
  currentStep: externalStep,
  onStepChange,
}) => {
  const [currentStep, setCurrentStep] = useState(
    externalStep !== undefined ? externalStep : loadTutorialState().tutorialStep
  );
  const [isVisible, setIsVisible] = useState(true);

  const steps: WalkthroughStep[] = [
    {
      title: 'Welcome to Free Forge!',
      message: "Let's create your first element: Fire + Water = Steam",
      highlight: 'elements-button',
      action: 'Tap the "Elements" button at the bottom',
      icon: <Sparkles size={24} />,
      position: 'bottom',
    },
    {
      title: 'Select Fire',
      message: 'Tap the Fire element from your elements panel.',
      highlight: 'fire-element',
      action: 'Tap Fire to place it in a drop zone',
      icon: <Hand size={24} />,
      position: 'bottom',
    },
    {
      title: 'Select Water',
      message: 'Great! Now tap the Water element.',
      highlight: 'water-element',
      action: 'Tap Water to place it in the second drop zone',
      icon: <Hand size={24} />,
      position: 'bottom',
    },
    {
      title: 'Combine!',
      message: 'Perfect! Now tap the "Combine" button to create Steam!',
      highlight: 'combine-button',
      action: 'Tap the Combine button',
      icon: <Sparkles size={24} />,
      position: 'bottom',
    },
    {
      title: 'Congratulations!',
      message: "You've discovered Steam! Keep experimenting to discover 100+ elements!",
      highlight: 'none',
      action: 'Continue playing',
      icon: <Sparkles size={24} />,
      position: 'bottom',
    },
  ];

  useEffect(() => {
    if (externalStep !== undefined && externalStep !== currentStep) {
      setCurrentStep(externalStep);
      updateTutorialStep(externalStep);
    }
  }, [externalStep]);

  const handleNext = () => {
    triggerHaptic('light');
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      updateTutorialStep(nextStep);
      if (onStepChange) {
        onStepChange(nextStep);
      }
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    triggerHaptic('light');
    markFirstCombinationComplete();
    onSkip();
  };

  const handleComplete = () => {
    triggerHaptic('medium');
    markFirstCombinationComplete();
    setIsVisible(false);
    setTimeout(onComplete, 300);
  };

  if (!isVisible) return null;

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <>
      {/* Subtle overlay - doesn't block pointer events */}
      {currentStep === 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.3)',
            zIndex: 9998,
            animation: 'fadeIn 0.3s ease-out',
            pointerEvents: 'none', // Allow clicks to pass through
          }}
        />
      )}

      {/* Compact Tutorial Card - Always at bottom */}
      <div
        style={{
          position: 'fixed',
          zIndex: 9999,
          left: '50%',
          bottom: 'max(env(safe-area-inset-bottom), 5.5rem)', // Above footer nav
          transform: 'translateX(-50%)',
          maxWidth: '95%',
          width: '340px',
          animation: 'slideUp 0.4s ease-out',
          pointerEvents: 'auto',
        }}
      >
        <div
          style={{
            background: `linear-gradient(135deg, ${colors.background.secondary}, #2d1b4e)`,
            borderRadius: '16px',
            padding: '1rem',
            border: `2px solid ${colors.brand.accent}`,
            boxShadow: `0 8px 24px rgba(101, 163, 13, 0.5)`,
            position: 'relative',
          }}
        >
          {/* Skip button */}
          {currentStep < steps.length - 1 && (
            <button
              onClick={handleSkip}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                color: colors.text.secondary,
                fontSize: '0.85rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                transition: 'color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colors.text.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = colors.text.secondary;
              }}
            >
              Skip
            </button>
          )}

          {/* Progress bar */}
          <div
            style={{
              height: '3px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
              marginBottom: '1rem',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                background: colors.gradients.primary,
                width: `${progress}%`,
                transition: 'width 0.4s ease',
                boxShadow: `0 0 10px ${colors.brand.accent}`,
              }}
            />
          </div>

          {/* Icon and Step number */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
            <div
              style={{
                color: colors.brand.accent,
                filter: `drop-shadow(0 0 10px ${colors.brand.accent})`,
              }}
            >
              {step.icon}
            </div>
            <div>
              <div
                style={{
                  fontSize: '0.7rem',
                  color: colors.text.secondary,
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Step {currentStep + 1} of {steps.length}
              </div>
              <h3
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: colors.text.primary,
                  margin: 0,
                }}
              >
                {step.title}
              </h3>
            </div>
          </div>

          {/* Message */}
          <p
            style={{
              color: colors.text.secondary,
              fontSize: '0.9rem',
              lineHeight: '1.4',
              marginBottom: '0.75rem',
            }}
          >
            {step.message}
          </p>

          {/* Action hint */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 10px',
              background: 'rgba(101, 163, 13, 0.15)',
              borderRadius: '8px',
              border: '1px solid rgba(101, 163, 13, 0.4)',
              marginBottom: '0.75rem',
            }}
          >
            <Hand size={14} style={{ color: colors.brand.accent, flexShrink: 0 }} />
            <span style={{ color: colors.brand.accent, fontSize: '0.85rem', fontWeight: '600' }}>
              {step.action}
            </span>
          </div>

          {/* Next/Complete button (only on first and last step) */}
          {(currentStep === 0 || currentStep === steps.length - 1) && (
            <button
              onClick={handleNext}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                border: 'none',
                background: colors.gradients.primary,
                color: 'white',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: `0 4px 16px ${colors.brand.accent}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 6px 20px ${colors.brand.accent}60`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 16px ${colors.brand.accent}40`;
              }}
            >
              {currentStep === 0 ? 'Start Tutorial' : 'Complete Tutorial'}
              {currentStep === steps.length - 1 ? <Sparkles size={20} /> : <ArrowRight size={20} />}
            </button>
          )}
        </div>

        {/* Pointer arrow (for non-center positions) */}
        {step.position !== 'center' && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              ...(step.position === 'top' && {
                bottom: '-10px',
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: `10px solid ${colors.brand.accent}`,
              }),
              ...(step.position === 'bottom' && {
                top: '-10px',
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderBottom: `10px solid ${colors.brand.accent}`,
              }),
            }}
          />
        )}
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translate(-50%, -30px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0);
            }
          }
        `}
      </style>
    </>
  );
};

export default InteractiveWalkthrough;
